import React from "react";

export const StationFilterContext = React.createContext();

export const SITE_CLASS_VS30_CATALOGS = ["SA", "SB", "SC", "SD", "SE"];

export const stationFilterDefaultState = {
  selectorType: "all", // 'all', 'byCode', or, 'byCondition'
  // Select by code
  stationCodeSelector: {},
  // Select by condition
  location: {
    // enabled: false,
    type: "", // 'boxRange' or 'circleRange'
    minLat: "",
    maxLat: "",
    minLon: "",
    maxLon: "",
    centralLat: "",
    centralLon: "",
    minRadius: "",
    maxRadius: "",
  },
  stationTypeSelector: {
    // enabled: false,
    hasSensorOnGround: false,
    hasSensorInBorehole: false,
    hasSensorOnGroundAndInBorehole: false,
  },
  siteClassVs30: {
    enabled: false,
    SA: false,
    SB: false,
    SC: false,
    SD: false,
    SE: false,
  },
};

export const stationFilterReducer = (state, action) => {
  switch (action.type) {
    case "SET_SELECTOR_TYPE": {
      const { value } = action;
      if (value !== state.selectorType) {
        return { ...state, selectorType: value };
      }
      return state;
    }
    case "SET_FILTER_VALUE": {
      const { category, key, value: rawValue } = action;
      const value = rawValue && rawValue.trim ? rawValue.trim() : rawValue;
      if (category && state[category]) {
        if (value !== state[category][key]) {
          return { ...state, [category]: { ...state[category], [key]: value } };
        }
      }
      return state;
    }
    case "SET_STATION_CODE_SELECTOR": {
      const { value } = action;
      return { ...state, stationCodeSelector: value };
    }
    case "SET_STATION_SELECT_STATE": {
      const { stationCode, value } = action;
      if (state.stationCodeSelector[stationCode] !== value) {
        return {
          ...state,
          stationCodeSelector: {
            ...state.stationCodeSelector,
            [stationCode]: value,
          },
        };
      }
      return state;
    }
    default:
      return state;
  }
};

// https://en.wikipedia.org/wiki/Earth_radius
const AVERAGE_EARTH_RADIUS_IN_KM = 6371;
const calculateDistance = (loc1, loc2) => {
  const toRadians = (value) => {
    return (value * Math.PI) / 180;
  };
  const dLat = toRadians(loc2.latitude - loc1.latitude);
  const dLon = toRadians(loc2.longitude - loc1.longitude);
  const lat1 = toRadians(loc1.latitude);
  const lat2 = toRadians(loc2.latitude);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = AVERAGE_EARTH_RADIUS_IN_KM * c;
  return d;
};

// https://www.thoughtco.com/degree-of-latitude-and-longitude-distance-4070616
const SIMPLE_KM_TO_DEGREE_OF_LATITUDE_CONVERSION_FACTOR = 112; // 110.567 ~ 111.699
export const selectStationsByCircleRange = (stations, filter) => {
  const { centralLat, centralLon, maxRadius } = filter;
  let selectedStations = [...stations];
  const preLatitudeFilter = {
    min:
      centralLat -
      maxRadius / SIMPLE_KM_TO_DEGREE_OF_LATITUDE_CONVERSION_FACTOR,
    max:
      centralLat +
      maxRadius / SIMPLE_KM_TO_DEGREE_OF_LATITUDE_CONVERSION_FACTOR,
  };
  // Do not filter by longitude because it requires additional complex calculations
  selectedStations = selectedStations.filter(
    ({ latitude }) =>
      latitude >= preLatitudeFilter.min && latitude <= preLatitudeFilter.max
  );
  const centerLocation = { latitude: centralLat, longitude: centralLon };
  return selectedStations.filter(
    ({ latitude, longitude }) =>
      calculateDistance({ latitude, longitude }, centerLocation) <= maxRadius
  );
};

export const selectStationsByCondition = (stations, state) => {
  const { location, stationTypeSelector, siteClassVs30 } = state;
  let selectedStations = [...stations];

  const {
    hasSensorOnGround,
    hasSensorInBorehole,
    hasSensorOnGroundAndInBorehole,
  } = stationTypeSelector;
  if (
    hasSensorOnGround ||
    hasSensorInBorehole ||
    hasSensorOnGroundAndInBorehole
  ) {
    if (!(hasSensorOnGround && hasSensorInBorehole)) {
      if (hasSensorOnGround) {
        // hasSensorOnGround is true (hasSensorOnGroundAndInBorehole doesn't mean anything)
        selectedStations = selectedStations.filter(
          ({ hasSensorOnGround }) => hasSensorOnGround
        );
      } else if (hasSensorInBorehole) {
        // hasSensorInBorehole is true (hasSensorOnGroundAndInBorehole doesn't mean anything)
        selectedStations = selectedStations.filter(
          ({ hasSensorInBorehole }) => hasSensorInBorehole
        );
      } else if (hasSensorOnGroundAndInBorehole) {
        // only hasSensorOnGroundAndInBorehole is true
        selectedStations = selectedStations.filter(
          ({ hasSensorOnGround, hasSensorInBorehole }) =>
            hasSensorOnGround && hasSensorInBorehole
        );
      }
    }
  }

  if (siteClassVs30.enabled) {
    const enabledVs30s = SITE_CLASS_VS30_CATALOGS.filter(
      (vs30) => siteClassVs30[vs30]
    );
    selectedStations = selectedStations.filter(({ site_class }) => {
      if (!site_class || !site_class.vs30) {
        return false;
      }
      const siteVs30Value =
        "S" +
        (site_class.vs30["Downhole test"] ||
          site_class.vs30["HVSR estimation"] ||
          site_class.vs30["P-wave estimation"] ||
          "");
      return enabledVs30s.includes(siteVs30Value);
    });
  }
  // console.log(selectedStations);

  if (location.type === "boxRange") {
    const locationFilter = {};
    const elements = [
      { key: "minLat", errorCodeAlias: "MinLat" },
      { key: "maxLat", errorCodeAlias: "MaxLat" },
      { key: "minLon", errorCodeAlias: "MinLon" },
      { key: "maxLon", errorCodeAlias: "MaxLon" },
    ];
    elements.forEach(({ key, errorCodeAlias }) => {
      if (location[key]) {
        const parsedValue = parseFloat(location[key]);
        if (Number.isNaN(parsedValue)) {
          throw new Error(`invalid${errorCodeAlias}Error`);
        }
        locationFilter[key] = parsedValue;
      }
    });
    if ("minLat" in locationFilter || "maxLat" in locationFilter) {
      const minLat = locationFilter.minLat || -90;
      const maxLat = locationFilter.maxLat || 90;
      selectedStations = selectedStations.filter(
        ({ latitude }) => latitude >= minLat && latitude <= maxLat
      );
    }
    if ("minLon" in locationFilter || "maxLon" in locationFilter) {
      const minLon = locationFilter.minLon || -180;
      const maxLon = locationFilter.maxLon || 180;
      selectedStations = selectedStations.filter(
        ({ longitude }) => longitude >= minLon && longitude <= maxLon
      );
    }
  } else if (location.type === "circleRange") {
    const locationFilter = {};
    const elements = [
      { key: "centralLat", errorCodeAlias: "CentralLat" },
      { key: "centralLon", errorCodeAlias: "CentralLon" },
      { key: "maxRadius", errorCodeAlias: "MaxRadius" },
    ];
    elements.forEach(({ key, errorCodeAlias }) => {
      if (location[key]) {
        const parsedValue = parseFloat(location[key]);
        if (Number.isNaN(parsedValue)) {
          throw new Error(`invalid${errorCodeAlias}Error`);
        }
        locationFilter[key] = parsedValue;
      } else {
        throw new Error(`no${errorCodeAlias}Error`);
      }
    });
    selectedStations = selectStationsByCircleRange(
      selectedStations,
      locationFilter
    );
  }

  return selectedStations;
};

export const selectStations = (stations, state) => {
  if (state.selectorType === "all") {
    return [...stations];
  } else if (state.selectorType === "byCondition") {
    return selectStationsByCondition(stations, state);
  } else if (state.selectorType === "byCode") {
    return stations.filter(({ code }) => state.stationCodeSelector[code]);
  }
  return stations.map(({ code }) => code);
};
